import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material";
import getParams from "../../hooks/useParams";
import { useQuery } from "react-query";
import {
  getUser,
  getQuoteFormData,
  getUserFromEmail,
  postCreateQuote,
  getSalesTax,
  postEditForm,
} from "../../api/QuoteFormApi";
import { UserObj } from "../../GlobalTypes";
import { findRequiredState } from "./form.biz";
import dealerRoles from "../../util/dealerAccounts";

export interface QuoteFormState {
  agSalesperson: string;
  salesperson: string;
  dealer: string;
  address: string;
  city: string;
  state?: string;
  zip: string;
  email: string;
  phone: string;
  leadNumber: string;
  customerName: string;
  jobName: string;
  customerEmail: string;
  customerPhone: string;
  customerZip: string;
  adminCOD: string;
  checkedDealer: boolean;
  checkedJobsite: boolean;
  formNotes: string;
  leadTime: boolean;
  customLeadTime: string;
  customerCity: string;
  heardAbout: string;
  heardAboutOther: string;
}

function useQuoteFormSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const id = getParams(location.pathname);
  const isQuoteCreated = location.state.isQuoteCreated || false;

  const {
    isLoading: isLoadingUser,
    error: userError,
    data: userData,
    isFetching: isFetchingUser,
  } = useQuery(
    ["User", { isQuoteCreated }],
    async () => {
      const response = await getUser(id);

      if (response.data.signInRole === "Production") {
        const user = JSON.parse(
          sessionStorage.getItem("user") || JSON.stringify("")
        );
        navigate(`/dealerZone/${user}`);
        return;
      }

      if (!isQuoteCreated) {
        const { role, agSalesRep, dealerRep, dealer, email, phone, state } =
          response.data;

        const hasDealerAccess = dealerRoles.includes(role);

        if (hasDealerAccess) {
          setState({
            ...state,
            agSalesperson: agSalesRep,
            salesperson: dealerRep,
            dealer,
            email,
            phone,
            state: state || "CA",
          });
        } else if (role === "Outside Sales") {
          setState({ ...state, agSalesperson: dealerRep });
        }

        !hasDealerAccess && handleOpen();
      }

      return response.data;
    },
    { refetchOnWindowFocus: false }
  );

  const { data, isFetching } = useQuery(
    ["Quote", { isQuoteCreated }],
    () => {
      if (isQuoteCreated) {
        return fetchQuote();
      }
    },
    { refetchOnWindowFocus: false }
  );

  const fetchQuote = async () => {
    try {
      const response = await getQuoteFormData(id);

      const { quote } = response.data;

      if (quote) {
        setState({
          agSalesperson: quote.agSalesperson,
          salesperson: quote.salesperson,
          dealer: quote.dealer,
          address: quote.address,
          city: quote.city,
          zip: quote.zip,
          state: quote.state || "CA",
          email: quote.email,
          phone: quote.phone,
          leadNumber: quote.leadNumber,
          customerName: quote.customerName,
          jobName: quote.jobName,
          customerEmail: quote.customerEmail,
          customerPhone: quote.customerPhone,
          customerZip: quote.customerZip,
          adminCOD: quote.adminCOD || "None",
          checkedDealer: quote.checkedDealer,
          checkedJobsite: quote.checkedJobsite,
          formNotes: quote.formNotes ? quote.formNotes : "",
          leadTime: quote.customLeadTime ? true : false,
          customLeadTime: quote.customLeadTime || "",
          customerCity: quote.customerCity || "",
          heardAbout: quote.heardAbout,
          heardAboutOther: quote.heardAboutOther,
        });

        return quote;
      }
    } catch (err) {
      return;
    }
  };

  const [err, setErr] = useState(false);

  const [addressErr, setAddressErr] = useState(false);

  const [state, setState] = useState<QuoteFormState>({
    agSalesperson: "",
    salesperson: "",
    dealer: "",
    address: "",
    city: "",
    zip: "",
    state: "",
    email: "",
    phone: "",
    leadNumber: "",
    customerName: "",
    jobName: "",
    customerEmail: "",
    customerPhone: "",
    customerZip: "",
    adminCOD: "None",
    checkedDealer: false,
    checkedJobsite: false,
    formNotes: "",
    leadTime: false,
    customLeadTime: "",
    customerCity: "",
    heardAbout: "",
    heardAboutOther: "",
  });

  const [requireds, setRequireds] = useState<string[]>([]);

  useEffect(() => {
    if (!state || isFetchingUser) return;
    setRequireds(findRequiredState(state, userData.signInRole));
  }, [state]);

  const handleAdress = (place: google.maps.places.PlaceResult) => {
    const formatted = place.formatted_address;
    if (!formatted) return;
    const separatedAddress = formatted.split(",");
    setState((prev) => ({
      ...prev,
      address: separatedAddress[0].trim(),
      city: separatedAddress[1].trim(),
      state: separatedAddress[2].trim().split(" ")[0],
      zip: separatedAddress[2].trim().split(" ")[1],
    }));
  };

  const handleAllChange = (
    event: React.ChangeEvent<any> | SelectChangeEvent<any>
  ) => {
    const { name, value } = event.target;
    let update = {
      ...state,
      [name]:
        name !== "leadTime" && name !== "glass"
          ? value
          : "checked" in event.target
          ? event.target.checked
          : "",
    };

    if (name === "heardAbout" && value !== "Other") {
      update["heardAboutOther"] = "";
    }

    setState(update);

    if (state.checkedDealer || state.checkedJobsite) {
      if (name === "customerZip" || name === "customerCity") {
        !hideCurrentTax && isQuoteCreated && setHCT(true);
        setTaxResults([]);
        setSalesTaxErr("");
      }
    }
  };

  const handleCheck = (delivery: string) => {
    const isNonAdmin = dealerRoles.includes(userData.role);

    if (delivery === "dealer") {
      if (!state.checkedDealer) {
        setState({
          ...state,
          address: userData.address || "",
          city: userData.city || "",
          zip: userData.zip || "",
          state: userData.state || "",
          checkedDealer: true,
          checkedJobsite: false,
        });
      } else {
        return;
      }
    } else if (delivery === "jobsite") {
      isNonAdmin
        ? setState({
            ...state,
            address: "",
            city: "",
            zip: "",
            checkedDealer: false,
            checkedJobsite: !state.checkedJobsite,
          })
        : setState({
            ...state,
            checkedDealer: false,
            checkedJobsite: !state.checkedJobsite,
          });
    }
  };

  const [salesTaxErr, setSalesTaxErr] = useState("");

  const handleDelete = () => console.info("You clicked the delete icon.");

  const handleReturn = () => {
    const loggedInUser = JSON.parse(
      sessionStorage.getItem("user") || JSON.stringify("")
    );
    navigate(`/dealerZone/${loggedInUser}`);
  };

  const [isBtnDisabled, setIsBtnDisabled] = useState(false);

  const checkForReqs = () => {
    if (!state.checkedDealer && !state.checkedJobsite) {
      setErr(true);
      return true;
    }

    if (
      !state.jobName ||
      !state.agSalesperson ||
      !state.dealer ||
      !state.salesperson ||
      !state.email ||
      (state.customerZip && !state.customerCity) ||
      (!state.customerZip && state.customerCity)
    ) {
      setErr(true);
      return true;
    }

    if (state.checkedDealer) {
      if (!state.address || !state.city || !state.zip) {
        setErr(true);
        return true;
      }
    }

    if (dealerRoles.includes(userData.signInRole)) {
      if (!state.customerName) {
        setErr(true);
        return true;
      }
    }

    return false;
  };

  const [taxResults, setTaxResults] = useState([]);

  const handleCreateQuote = async () => {
    var requireds = checkForReqs();

    if (!requireds) {
      setIsBtnDisabled(true);
      setErr(false);
      localStorage.clear();

      const sTax = await handleSalesTax();

      if (sTax?.stErr) return setIsBtnDisabled(false);

      if (userData.role !== "Dealer") {
        const res = await getUserFromEmail(state.email.toLowerCase());

        if (res.data.error) return setIsBtnDisabled(false);

        const quoteID =
          res.data === "no user found with that email" ? id : res.data.id;

        createQuote(quoteID, sTax?.salesTax || null);
      } else {
        const res = await postCreateQuote({
          userId: id,
          quoteForm: state,
          salesTax: sTax?.salesTax || null,
        });

        if (!res) return setIsBtnDisabled(false);

        sessionStorage.setItem("quoteId", JSON.stringify(res.data.quoteId));
        sessionStorage.setItem(
          "quoteNumber",
          JSON.stringify(res.data.quoteNumber)
        );

        navigate(`/config/${id}`, {
          state: { isQuoteCreated: true, userId: id },
        });
      }
    }
  };

  const createQuote = async (userIdid: string, salesTax: number | null) => {
    const res = await postCreateQuote({
      userId: userIdid,
      quoteForm: state,
      salesTax,
    });

    if (!res) return setIsBtnDisabled(false);

    sessionStorage.setItem("quoteId", JSON.stringify(res.data.quoteId));
    sessionStorage.setItem("quoteNumber", JSON.stringify(res.data.quoteNumber));

    navigate(`/config/${id}`, {
      state: { isQuoteCreated: true, userId: userIdid },
    });
  };

  const handleSalesTax = async () => {
    let salesTax = 0;
    let stErr = false;
    const zip = state.customerZip || null;
    const salesTaxCity = state.customerCity || null;

    if (taxResults.length && tax && zip) {
      salesTax = tax;
    } else if (zip) {
      const res = await getSalesTax(zip, salesTaxCity);

      if (res.data.error) {
        salesTax = 0;
        stErr = true;
        setSalesTaxErr(res.data.error);
        return setTaxResults([]);
      }

      setSalesTaxErr("");
      if (res.data.results.length > 1) {
        setTaxResults(res.data.results);
        stErr = true;
      } else {
        salesTax = res.data.results[0];
        stErr = false;
        setTaxResults([]);
      }
    }

    return { salesTax, stErr };
  };

  const handleUpdateQuote = async () => {
    var requireds = checkForReqs();

    if (requireds) return;

    setIsBtnDisabled(true);
    setErr(false);

    const sTax = await handleSalesTax();

    if (sTax?.stErr) return setIsBtnDisabled(false);

    const quoteId = JSON.parse(
      sessionStorage.getItem("quoteId") || JSON.stringify("")
    );
    const res = await postEditForm({
      quoteId: quoteId,
      updatedForm: state,
      salesTax: sTax?.salesTax || null,
    });

    if (res.data.data) {
      navigate(`/config/${id}`, {
        state: { isQuoteCreated: true, userId: id },
      });
      return;
    }

    setIsBtnDisabled(false);
  };

  const returnToConfig = () =>
    navigate(`/config/${id}`, {
      state: { isQuoteCreated: true, userId: id },
    });

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  const adminFillFields = (dealer: UserObj) => {
    if (!dealer) return;

    setState({
      ...state,
      agSalesperson: dealer.agSalesRep || "",
      salesperson: dealer.dealerRep || "",
      dealer: dealer.dealer || "",
      email: dealer.email || "",
      phone: dealer.phone || "",
      address: dealer.address || "",
      city: dealer.city || "",
      state: dealer.state || "CA",
      zip: dealer.zip || "",
      adminCOD: dealer.codOrTerms || "None",
      checkedDealer: true,
      checkedJobsite: false,
    });
    handleClose();
  };

  const [tax, setTax] = useState(0);

  const handleTaxChange = (e: SelectChangeEvent<string>) =>
    setTax(Number(e.target.value));

  const [hideCurrentTax, setHCT] = useState(false);
  return {
    handleTaxChange,
    tax,
    adminFillFields,
    handleClose,
    handleOpen,
    open,
    returnToConfig,
    handleUpdateQuote,
    handleCreateQuote,
    taxResults,
    isBtnDisabled,
    handleReturn,
    handleDelete,
    salesTaxErr,
    handleCheck,
    handleAllChange,
    requireds,
    state,
    err,
    data,
    isFetching,
    isLoadingUser,
    userError,
    userData,
    isFetchingUser,
    isQuoteCreated,
    location,
    hideCurrentTax,
    handleAdress,
    addressErr,
    setAddressErr
  };
}

export default useQuoteFormSelector;
